<template>
    <div class="modal fade segment-proposal-modal" :id="modalId" data-bs-backdrop="static" aria-labelledby="segment-proposal-modal-title" aria-hidden="true">
        <div class="modal-dialog modal-lg modal-dialog-scrollable">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="d-flex align-items-start w-100">
                        <div class="flex-grow-1" style="min-width: 0; margin-right: 1rem;">
                            <h3 class="mt-2 mb-2 text-center text-sm-start">Suggested Audience</h3>
                        </div>
                        <div class="d-flex align-items-center" style="flex: 0 0 auto;">
                            <button type="button" class="btn btn-white close-modal" data-bs-dismiss="modal" aria-label="Close" @click="handleClose">
                                <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt="">
                            </button>
                        </div>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="loading" class="text-center py-4">
                        <div class="spinner-border text-primary" role="status"></div>
                        <p class="mt-2">Loading suggestion...</p>
                    </div>
                    <div v-else-if="error" class="alert alert-danger">{{ error }}</div>
                    <div v-else-if="suggestion" class="d-flex flex-column gap-3">
                        <div class="card segment-proposal-card">
                            <div class="accordion accordion-flush" :id="`${modalId}-accordion-audience-info`">
                                <div class="accordion-item border-0 bg-white">
                                    <h2 class="accordion-header">
                                        <button class="accordion-button collapsed py-3 h6 fw-semibold mb-0" type="button" data-bs-toggle="collapse" :data-bs-target="`#${modalId}-collapse-audience-info`" :aria-expanded="false" :aria-controls="`${modalId}-collapse-audience-info`">
                                            Audience Information
                                        </button>
                                    </h2>
                                    <div :id="`${modalId}-collapse-audience-info`" class="accordion-collapse collapse" :data-bs-parent="`#${modalId}-accordion-audience-info`">
                                        <div class="accordion-body p-3 pt-0">
                                            <div class="mb-3">
                                                <label class="form-label">Audience Name</label>
                                                <input type="text" class="form-control bg-light" :value="suggestion.name" disabled readonly aria-label="Audience Name">
                                            </div>
                                            <div>
                                                <label class="form-label">What would motivate this audience to take action?</label>
                                                <textarea class="form-control bg-light" :value="suggestion.value_prop" rows="4" disabled readonly></textarea>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <FilterDefinitionCard
                            :filter="suggestion.filter"
                            title="Audience Definition"
                            :contact-count="segmentSize"
                        />
                    </div>
                </div>
                <div v-if="!loading && !error && suggestion" class="modal-footer border-top-0 pt-0">
                    <button type="button" class="btn btn-secondary" @click="saveAudience(false)" :disabled="submitting">
                        <img class="icon me-2" src="/img/icons/avid-icons/edit.svg" alt=""> Edit
                    </button>
                    <button type="button" class="btn btn-primary" @click="saveAudience(true)" :disabled="submitting">
                        <span v-if="submitting" class="spinner-border spinner-border-sm me-2" role="status"></span>
                        <img v-else class="icon me-2" src="/img/icons/avid-icons/save.svg" alt=""> Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import { useNotifications } from '../../composables/useNotifications';
import FilterDefinitionCard from '../components/FilterDefinitionCard.vue';

export default defineComponent({
    name: 'SegmentProposalModal',
    components: { FilterDefinitionCard },
    props: {
        client: { type: Object, required: true },
    },
    data() {
        return {
            modalId: 'segment-proposal-modal-' + Date.now(),
            segmentSuggestionId: null,
            suggestion: null,
            loading: false,
            error: null,
            submitting: false,
            segmentSize: null,
            justAccepted: false,
        };
    },
    mounted() {
        const { showNotification } = useNotifications();
        this.showNotification = showNotification;
        const modalEl = document.getElementById(this.modalId);
        if (modalEl) {
            modalEl.addEventListener('hidden.bs.modal', this.onModalHidden);
        }
    },
    beforeUnmount() {
        const modalEl = document.getElementById(this.modalId);
        if (modalEl) {
            modalEl.removeEventListener('hidden.bs.modal', this.onModalHidden);
        }
    },
    methods: {
        show(segmentSuggestionId) {
            this.segmentSuggestionId = segmentSuggestionId;
            this.justAccepted = false;
            this.suggestion = null;
            this.segmentSize = null;
            this.error = null;
            this.loading = true;
            const modalEl = document.getElementById(this.modalId);
            if (modalEl && window.bootstrap) {
                let modal = window.bootstrap.Modal.getInstance(modalEl);
                if (!modal) modal = new window.bootstrap.Modal(modalEl);
                modal.show();
            }
            this.loadSuggestion();
        },
        async loadSuggestion() {
            if (!this.segmentSuggestionId || !this.client?.url) {
                this.loading = false;
                return;
            }
            try {
                const response = await window.axios.get(`/api/segment-suggestion/${this.client.url}/${this.segmentSuggestionId}`);
                this.suggestion = response.data.segment_suggestion || null;
                if (!this.suggestion) this.error = 'Suggestion not found.';
                else this.getSegmentSize();
            } catch (e) {
                this.error = e.response?.data?.error || e.response?.data?.message || 'Failed to load suggestion.';
            } finally {
                this.loading = false;
            }
        },
        numberWithZero(num) {
            if (num === null || num === undefined) return '0';
            return Number(num).toLocaleString();
        },
        getSegmentSize() {
            if (!this.suggestion?.filter?.logic_units?.length || !this.suggestion.data_blend_id || !this.client?.url) {
                this.segmentSize = this.suggestion?.filter?.logic_units?.length ? -1 : null;
                return;
            }
            this.segmentSize = null;
            const formData = {
                filter: this.suggestion.filter,
                source: 'data_blend',
                data_source_id: this.suggestion.data_blend_id,
            };
            window.axios
                .post(`/api/segment/${this.client.url}/segment_size`, formData)
                .then((response) => {
                    this.segmentSize = response.data.size;
                })
                .catch(() => {
                    this.segmentSize = -1;
                });
        },
        async saveAudience(saveOnly) {
            if (!this.suggestion || !this.segmentSuggestionId) return;
            this.submitting = true;
            try {
                const response = await window.axios.post(`/api/segment-suggestion/${this.client.url}/accept`, {
                    segment_suggestion_id: this.segmentSuggestionId,
                    edit: !saveOnly,
                });
                this.justAccepted = true;
                const modalEl = document.getElementById(this.modalId);
                if (modalEl && window.bootstrap) {
                    const modal = window.bootstrap.Modal.getInstance(modalEl);
                    if (modal) modal.hide();
                }
                this.$emit('saved', { segment: response.data.segment, redirect_url: response.data.redirect_url });
                if (response.data.redirect_url) {
                    window.location.href = response.data.redirect_url;
                }
            } catch (e) {
                this.showNotification({ text: e.response?.data?.error || e.response?.data?.message || 'Failed to save audience.', type: 'error' });
            } finally {
                this.submitting = false;
            }
        },
        onModalHidden() {
            if (this.segmentSuggestionId && !this.justAccepted && this.client?.url) {
                window.axios.post(`/api/segment-suggestion/${this.client.url}/reject`, {
                    segment_suggestion_id: this.segmentSuggestionId,
                }).catch(() => {});
            }
            this.justAccepted = false;
        },
        handleClose() {},
    },
});
</script>
